/**
 * Open-Meteo forecast integration (SPEC §3.3).
 *
 * Free, no API key, CC-BY.
 *
 * The global `best_match` run is always fetched, because it is the only source
 * of the pressure-level sounding and the boundary-layer depth. Where a
 * high-resolution regional model exists — ICON-CH1 over Switzerland, AROME over
 * France, both of which resolve Alpine valley winds the global models cannot —
 * its near-surface fields are fetched separately and overlaid on top. See
 * `REGIONAL_VARS` for why the two cannot be merged into one request.
 */

import { deriveStability, type SoundingLevel, type StabilityState } from '../physics/stability';

const ENDPOINT = 'https://api.open-meteo.com/v1/forecast';

/**
 * Pressure levels requested for the sounding.
 *
 * 600 and 500 hPa are here for high sites. Below-ground levels are discarded
 * (see `sounding` construction), and at an Alpine launch that can remove 925,
 * 850 and even 700 hPa — at Jungfraujoch, where the model surface sits at
 * 3496 m, all three of the original levels are underground. Without something
 * higher there would be no sounding left to derive stability from.
 */
const PRESSURE_LEVELS = [925, 850, 700, 600, 500] as const;

const HOURLY = [
  'wind_speed_10m',
  'wind_direction_10m',
  'wind_speed_80m',
  'wind_direction_80m',
  'wind_speed_120m',
  'wind_direction_120m',
  'wind_speed_180m',
  'wind_direction_180m',
  'boundary_layer_height',
  'cape',
  'convective_inhibition',
  'temperature_2m',
  'surface_pressure',
  ...PRESSURE_LEVELS.flatMap((p) => [
    `wind_speed_${p}hPa`,
    `wind_direction_${p}hPa`,
    `temperature_${p}hPa`,
    `geopotential_height_${p}hPa`,
  ]),
];

export type ModelId = 'best_match' | 'meteoswiss_icon_ch1' | 'meteofrance_arome_france_hd';

export interface ModelChoice {
  id: ModelId;
  label: string;
}

export const MODELS: ModelChoice[] = [
  { id: 'best_match', label: 'Best match (auto)' },
  { id: 'meteoswiss_icon_ch1', label: 'MeteoSwiss ICON-CH1 (1 km, Alps)' },
  { id: 'meteofrance_arome_france_hd', label: 'Météo-France AROME HD (France)' },
];

/** Pick the highest-resolution model whose domain covers the point. */
export function preferredModel(lat: number, lon: number): ModelId {
  if (lat > 45.6 && lat < 48.0 && lon > 5.8 && lon < 10.6) return 'meteoswiss_icon_ch1';
  if (lat > 41.0 && lat < 51.5 && lon > -5.5 && lon < 10.0) return 'meteofrance_arome_france_hd';
  return 'best_match';
}

export interface WindAtHeight {
  height: number;
  speed: number;
  directionDeg: number;
}

export interface ForecastHour {
  time: string;
  /** local hour label, e.g. "14:00" */
  label: string;
  winds: WindAtHeight[];
  blHeight: number;
  cape: number;
  cin: number;
  temperature2m: number;
  surfacePressure: number;
  sounding: SoundingLevel[];
  stability: StabilityState;
}

export interface Forecast {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  model: ModelId;
  hours: ForecastHour[];
  attribution: string;
}

interface RawResponse {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  utc_offset_seconds: number;
  hourly: Record<string, (number | null)[] | string[]>;
}

/**
 * Variables the high-resolution regional models actually serve.
 *
 * ICON-CH1 and AROME are convection-permitting local-area models: they do not
 * publish pressure-level fields or a diagnosed boundary-layer height, and
 * ICON-CH1 does not publish the 80/120/180 m winds either. Asking them for
 * those returns nulls, not an error — which silently left the stability dial at
 * "no sounding, assuming neutral" over the Alps, the one region the regional
 * model was selected to improve.
 */
const REGIONAL_VARS = [
  'wind_speed_10m',
  'wind_direction_10m',
  'wind_speed_80m',
  'wind_direction_80m',
  'wind_speed_120m',
  'wind_direction_120m',
  'wind_speed_180m',
  'wind_direction_180m',
  'cape',
  'convective_inhibition',
  'temperature_2m',
  'surface_pressure',
];

async function request(
  lat: number,
  lon: number,
  vars: string[],
  model: ModelId | null,
  signal?: AbortSignal
): Promise<RawResponse> {
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    hourly: vars.join(','),
    timezone: 'auto',
    forecast_days: '3',
    wind_speed_unit: 'ms',
  });
  if (model && model !== 'best_match') params.set('models', model);

  const res = await fetch(`${ENDPOINT}?${params}`, { signal });
  if (!res.ok) throw new Error(`Open-Meteo returned ${res.status}`);
  return (await res.json()) as RawResponse;
}

export async function fetchForecast(
  lat: number,
  lon: number,
  model: ModelId,
  signal?: AbortSignal
): Promise<Forecast> {
  // The global run is always fetched, because it is the only source of the
  // sounding and the boundary-layer depth. A regional model, when chosen, is
  // then overlaid for the near-surface fields it resolves better.
  const raw = await request(lat, lon, HOURLY, 'best_match', signal);

  if (model !== 'best_match') {
    try {
      const regional = await request(lat, lon, REGIONAL_VARS, model, signal);
      overlay(raw, regional, REGIONAL_VARS);
    } catch {
      // A regional model that is down or out of domain is not worth failing
      // the whole forecast for; the global values are already in place.
    }
  }

  const times = raw.hourly.time as string[];
  const num = (k: string): (number | null)[] =>
    (raw.hourly[k] as (number | null)[] | undefined) ?? [];

  const get = (k: string, i: number, fallback = NaN): number => {
    const v = num(k)[i];
    return v === null || v === undefined ? fallback : v;
  };

  const hours: ForecastHour[] = times.map((time, i) => {
    const winds: WindAtHeight[] = [10, 80, 120, 180]
      .map((h) => ({
        height: h,
        speed: get(`wind_speed_${h}m`, i),
        directionDeg: get(`wind_direction_${h}m`, i),
      }))
      .filter((w) => Number.isFinite(w.speed) && Number.isFinite(w.directionDeg));

    /**
     * Levels below the model's own surface are discarded.
     *
     * Open-Meteo reports every pressure level everywhere, extrapolating the
     * ones that fall underground. At Zermatt (model surface 1608 m) both 925
     * and 850 hPa sit below the ground; at Jungfraujoch (3496 m) so does 700.
     * Keeping them meant the Brunt–Väisälä frequency was computed between two
     * fictitious sub-surface levels, and N² sets the Froude regime, the wake
     * length and whether Layer 1 sends air over terrain or around it — so an
     * Alpine site was being flown on a stability number describing rock.
     */
    const sounding: SoundingLevel[] = PRESSURE_LEVELS.map((p) => ({
      pressure: p,
      temperature: get(`temperature_${p}hPa`, i),
      height: get(`geopotential_height_${p}hPa`, i),
    })).filter(
      (l) =>
        Number.isFinite(l.temperature) &&
        Number.isFinite(l.height) &&
        l.height > raw.elevation
    );

    // Anchor the sounding at the surface so the lowest layer — the one that
    // actually decides whether a morning inversion is present — is included.
    const t2 = get('temperature_2m', i);
    const sp = get('surface_pressure', i);
    if (Number.isFinite(t2) && Number.isFinite(sp)) {
      sounding.unshift({ pressure: sp, temperature: t2, height: raw.elevation + 2 });
    }

    const blHeight = get('boundary_layer_height', i, 1200);
    const cape = get('cape', i, 0);

    return {
      time,
      label: time.slice(11, 16),
      winds,
      blHeight,
      cape,
      cin: get('convective_inhibition', i, 0),
      temperature2m: t2,
      surfacePressure: sp,
      sounding,
      stability: deriveStability(sounding, { cape, blHeight }),
    };
  });

  return {
    latitude: raw.latitude,
    longitude: raw.longitude,
    elevation: raw.elevation,
    timezone: raw.timezone,
    model,
    hours,
    attribution: 'Weather data by Open-Meteo.com (CC-BY 4.0)',
  };
}

/**
 * Copy non-null regional values over the global ones, matched by timestamp.
 * Timestamps are matched rather than indexed because the two runs can start at
 * different hours.
 */
function overlay(base: RawResponse, extra: RawResponse, vars: string[]): void {
  const baseTimes = base.hourly.time as string[];
  const extraTimes = extra.hourly.time as string[] | undefined;
  if (!extraTimes) return;

  const indexOf = new Map<string, number>();
  extraTimes.forEach((t, i) => indexOf.set(t, i));

  for (const key of vars) {
    const src = extra.hourly[key] as (number | null)[] | undefined;
    if (!src) continue;
    const dst = base.hourly[key] as (number | null)[] | undefined;
    if (!dst) continue;
    for (let i = 0; i < baseTimes.length; i++) {
      const j = indexOf.get(baseTimes[i]);
      if (j === undefined) continue;
      const v = src[j];
      if (v !== null && v !== undefined) dst[i] = v;
    }
  }
}

/**
 * Free-stream wind for a given height above the terrain.
 *
 * Interpolates between the model's fixed heights, and extrapolates above the
 * top level with a mild power law rather than holding the value flat, which
 * would understate the gradient wind at 500 m over a ridge.
 */
export function windAtHeight(hour: ForecastHour, heightAGL: number): WindAtHeight {
  const w = hour.winds;
  if (w.length === 0) return { height: heightAGL, speed: 0, directionDeg: 0 };
  if (heightAGL <= w[0].height) return { ...w[0], height: heightAGL };

  for (let i = 0; i < w.length - 1; i++) {
    if (heightAGL <= w[i + 1].height) {
      const t = (heightAGL - w[i].height) / (w[i + 1].height - w[i].height);
      return {
        height: heightAGL,
        speed: w[i].speed + (w[i + 1].speed - w[i].speed) * t,
        directionDeg: interpolateDegrees(w[i].directionDeg, w[i + 1].directionDeg, t),
      };
    }
  }

  const top = w[w.length - 1];
  return {
    height: heightAGL,
    speed: top.speed * Math.pow(heightAGL / top.height, 0.11),
    directionDeg: top.directionDeg,
  };
}

/** Shortest-arc interpolation, so 350° → 10° goes through 0 and not the long way. */
export function interpolateDegrees(a: number, b: number, t: number): number {
  const d = ((b - a + 540) % 360) - 180;
  return (a + d * t + 360) % 360;
}

/** Index of the hour nearest to `when`, for the time scrubber. */
export function nearestHourIndex(forecast: Forecast, when: Date): number {
  let best = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < forecast.hours.length; i++) {
    const diff = Math.abs(new Date(forecast.hours[i].time).getTime() - when.getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best;
}
